import { DAYS } from './DAYS';

export default interface MailingObject {
    uuid: string;
    emails: string[];
    mailingType: string;
    cron: string;
    targetObjectUuid?: string;
    targetObjectLabel?: string;
}

export function getMailingTypeInfo(type: MailingTypes): [MailingTypes, string, boolean] {
    switch (type) {
        case MailingTypes.EMPLOYEE_BIRTHDAY_REMINDER:
            return [MailingTypes.EMPLOYEE_BIRTHDAY_REMINDER, 'Employee birthday reminders', true];
        case MailingTypes.EMPLOYEE_WORK_ANNIVERSARY_REMINDER:
            return [MailingTypes.EMPLOYEE_WORK_ANNIVERSARY_REMINDER, 'Employee work anniversary reminders', true];
        case MailingTypes.TIMESHEET_READY_FOR_REVIEW:
            return [MailingTypes.TIMESHEET_READY_FOR_REVIEW, 'Receive a reminder whenever a timesheet has been put op for review', false];
        case MailingTypes.TIMESHEETS_SHOULD_GET_UPLOADED_REMINDER:
            return [MailingTypes.TIMESHEETS_SHOULD_GET_UPLOADED_REMINDER, 'Receive a reminder to tell your employees to upload their timesheets', true];
        case MailingTypes.DEAL_ABOUT_TO_ROT_REMINDER:
            return [MailingTypes.DEAL_ABOUT_TO_ROT_REMINDER, 'Receive a reminder whenever a deal is about to rot', true];
        case MailingTypes.INVENTORY_LOW_STOCK_REMINDER:
            return [MailingTypes.INVENTORY_LOW_STOCK_REMINDER, 'Receive a list of all products that are low or out of stock', true];
        case MailingTypes.BUDGET_DUE_REMINDER:
            return [MailingTypes.BUDGET_DUE_REMINDER, 'Receive a list of all budgets that are almost over time', true];
        case MailingTypes.PROJECT_WARRANTY_RUN_OUT_REMINDER:
            return [MailingTypes.PROJECT_WARRANTY_RUN_OUT_REMINDER, 'Receive a list of all projects with a warranty running out soon', true];
        case MailingTypes.PROJECT_WARRANTY_EXPIRES_REMINDER:
            return [MailingTypes.PROJECT_WARRANTY_EXPIRES_REMINDER, 'Receive a list of all projects with an expiring warranty', true];
        case MailingTypes.PROJECT_BUDGET_LIMIT_APPROACHING_REMINDER:
            return [MailingTypes.PROJECT_BUDGET_LIMIT_APPROACHING_REMINDER, 'Receive a list of ongoing project budgets at or above 80%', true];
        case MailingTypes.TARGETS_OVERVIEW_REMINDER:
            return [MailingTypes.TARGETS_OVERVIEW_REMINDER, 'Receive a weekly overview of the revenue targets status', true];
        case MailingTypes.BUSINESS_DEVELOPER_MARGIN_REPORT:
            return [MailingTypes.BUSINESS_DEVELOPER_MARGIN_REPORT, 'Receive a portfolio margin report for one selected business developer', true];
        case MailingTypes.OVERHEAD_BUDGETS_OVERVIEW_REMINDER:
            return [MailingTypes.OVERHEAD_BUDGETS_OVERVIEW_REMINDER, 'Receive an overview of overhead budget warnings', true];
        case MailingTypes.EMPLOYEE_MONTHLY_PERFORMANCE_REMINDER:
            return [MailingTypes.EMPLOYEE_MONTHLY_PERFORMANCE_REMINDER, 'Receive a monthly employee performance overview sorted by margin %', true];
        case MailingTypes.RESOURCE_BUDGET_APPROACHING_LIMIT_REMINDER:
            return [
                MailingTypes.RESOURCE_BUDGET_APPROACHING_LIMIT_REMINDER,
                'Receive a list of resource budgets at or above 80% of their limit (incl. amount over budget)',
                true,
            ];
        case MailingTypes.YUKI_NEW_INVOICES_REMINDER:
            return [
                MailingTypes.YUKI_NEW_INVOICES_REMINDER,
                'Receive a mail with newly synced Yuki invoices and AI-suggested allocations (after each nightly sync)',
                false,
            ];
        case MailingTypes.YUKI_INVOICES_TO_VALIDATE_REMINDER:
            return [MailingTypes.YUKI_INVOICES_TO_VALIDATE_REMINDER, 'Receive a summary of purchase and sales invoices that still need validation', true];
        case MailingTypes.FLEET_DOCUMENTS_UPLOADED:
            return [MailingTypes.FLEET_DOCUMENTS_UPLOADED, 'Receive a mail when a driver has uploaded the requested vehicle documents', false];
    }
}

export function getDescription(cron: string): string {
    let description = '';
    if (cron == '0 0 0 1/1 * ? *') {
        description = 'Daily';
    } else if (cron.includes('0 0 0 ? * ')) {
        description += 'Every ';
        description += DAYS.findDay(cron).long;
    } else if (cron.includes('0 0 0 ') && cron.includes(' * ? *')) {
        if (cron.includes('L-')) {
            const days = cron.slice(8, 9);
            if (days == '0') {
                description = 'The last day of the month.';
            } else if (days == '1') {
                description = 'One day before the end of the month.';
            } else {
                description = days + ' days before the end of the month.';
            }
        } else {
            const days = cron.slice(6, 7);
            if (days == '1') {
                description = 'First day of the month.';
            } else {
                description = 'Day ' + days + ' of the month.';
            }
        }
    }

    return description;
}

export enum MailingTypes {
    EMPLOYEE_BIRTHDAY_REMINDER = 'EMPLOYEE_BIRTHDAY_REMINDER',
    EMPLOYEE_WORK_ANNIVERSARY_REMINDER = 'EMPLOYEE_WORK_ANNIVERSARY_REMINDER',
    TIMESHEET_READY_FOR_REVIEW = 'TIMESHEET_READY_FOR_REVIEW',
    TIMESHEETS_SHOULD_GET_UPLOADED_REMINDER = 'TIMESHEETS_SHOULD_GET_UPLOADED_REMINDER',
    DEAL_ABOUT_TO_ROT_REMINDER = 'DEAL_ABOUT_TO_ROT_REMINDER',
    INVENTORY_LOW_STOCK_REMINDER = 'INVENTORY_LOW_STOCK_REMINDER',
    BUDGET_DUE_REMINDER = 'BUDGET_DUE_REMINDER',
    PROJECT_WARRANTY_RUN_OUT_REMINDER = 'PROJECT_WARRANTY_RUN_OUT_REMINDER',
    PROJECT_WARRANTY_EXPIRES_REMINDER = 'PROJECT_WARRANTY_EXPIRES_REMINDER',
    PROJECT_BUDGET_LIMIT_APPROACHING_REMINDER = 'PROJECT_BUDGET_LIMIT_APPROACHING_REMINDER',
    TARGETS_OVERVIEW_REMINDER = 'TARGETS_OVERVIEW_REMINDER',
    BUSINESS_DEVELOPER_MARGIN_REPORT = 'BUSINESS_DEVELOPER_MARGIN_REPORT',
    OVERHEAD_BUDGETS_OVERVIEW_REMINDER = 'OVERHEAD_BUDGETS_OVERVIEW_REMINDER',
    EMPLOYEE_MONTHLY_PERFORMANCE_REMINDER = 'EMPLOYEE_MONTHLY_PERFORMANCE_REMINDER',
    RESOURCE_BUDGET_APPROACHING_LIMIT_REMINDER = 'RESOURCE_BUDGET_APPROACHING_LIMIT_REMINDER',
    YUKI_NEW_INVOICES_REMINDER = 'YUKI_NEW_INVOICES_REMINDER',
    YUKI_INVOICES_TO_VALIDATE_REMINDER = 'YUKI_INVOICES_TO_VALIDATE_REMINDER',
    FLEET_DOCUMENTS_UPLOADED = 'FLEET_DOCUMENTS_UPLOADED',
}

export function getMailingTypesByModule(moduleKey: string): MailingTypes[] {
    switch (moduleKey) {
        case 'EMPLOYEES':
            return [MailingTypes.EMPLOYEE_BIRTHDAY_REMINDER, MailingTypes.EMPLOYEE_WORK_ANNIVERSARY_REMINDER];
        case 'TIMESHEETS':
            return [MailingTypes.TIMESHEET_READY_FOR_REVIEW, MailingTypes.TIMESHEETS_SHOULD_GET_UPLOADED_REMINDER];
        case 'CRM':
            return [MailingTypes.DEAL_ABOUT_TO_ROT_REMINDER];
        case 'INVENTORY':
            return [MailingTypes.INVENTORY_LOW_STOCK_REMINDER];
        case 'BUDGET':
            return [MailingTypes.BUDGET_DUE_REMINDER, MailingTypes.RESOURCE_BUDGET_APPROACHING_LIMIT_REMINDER];
        case 'PROJECTS':
            return [
                MailingTypes.PROJECT_WARRANTY_RUN_OUT_REMINDER,
                MailingTypes.PROJECT_WARRANTY_EXPIRES_REMINDER,
                MailingTypes.PROJECT_BUDGET_LIMIT_APPROACHING_REMINDER,
            ];
        case 'FINANCIAL':
            return [
                MailingTypes.TARGETS_OVERVIEW_REMINDER,
                MailingTypes.BUSINESS_DEVELOPER_MARGIN_REPORT,
                MailingTypes.EMPLOYEE_MONTHLY_PERFORMANCE_REMINDER,
                MailingTypes.OVERHEAD_BUDGETS_OVERVIEW_REMINDER,
            ];
        case 'YUKI':
            return [MailingTypes.YUKI_NEW_INVOICES_REMINDER, MailingTypes.YUKI_INVOICES_TO_VALIDATE_REMINDER];
        case 'FLEET':
            return [MailingTypes.FLEET_DOCUMENTS_UPLOADED];
        default:
            return [];
    }
}
